import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSimulador } from '../context/SimuladorContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Users, Plus, X, Maximize2, Minimize2, RefreshCw,
  Search, Monitor, ChevronDown, ChevronUp, Expand, Shrink,
  Minus, Square
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SimuladorFlutuante() {
  const {
    operadoresAtivos,
    isMinimizado,
    isAberto,
    gridCols,
    fullscreenOperador,
    setGridCols,
    adicionarOperador,
    removerOperador,
    removerTodos,
    toggleMinimizar,
    abrirSimulador,
    fecharSimulador,
    toggleFullscreen,
    recarregarOperador
  } = useSimulador();

  const [operadores, setOperadores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSelectorModal, setShowSelectorModal] = useState(false);

  useEffect(() => {
    if (showSelectorModal && operadores.length === 0) {
      loadOperadores();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSelectorModal]);

  const loadOperadores = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/operadores`);
      setOperadores(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Erro ao carregar operadores:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAdicionarOperador = (operador) => {
    if (adicionarOperador(operador)) {
      setShowSelectorModal(false);
    }
  };

  const operadoresFiltrados = operadores.filter(op => {
    if (!op.nome && !op.username) return false;
    if (!searchTerm) return true;
    const search = searchTerm.toLowerCase();
    return (op.nome || '').toLowerCase().includes(search) ||
           (op.username || '').toLowerCase().includes(search);
  });

  const getGridClass = () => {
    switch (gridCols) {
      case 2: return 'grid-cols-1 sm:grid-cols-2 justify-items-center';
      case 3: return 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 justify-items-center';
      case 4: return 'grid-cols-2 lg:grid-cols-4 justify-items-center';
      case 5: return 'grid-cols-2 lg:grid-cols-5 justify-items-center';
      default: return 'grid-cols-2 justify-items-center';
    }
  };

  // Não renderizar nada se não estiver aberto
  if (!isAberto) {
    return null;
  }

  // Modo minimizado - barra compacta no fundo
  if (isMinimizado) {
    return (
      <div 
        className="fixed bottom-0 left-64 right-0 z-40 bg-indigo-600 text-white shadow-lg"
        data-testid="simulador-minimizado"
      >
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <Monitor className="h-5 w-5" />
            <span className="font-medium">Simulador de Operadores</span>
            <span className="bg-white/20 px-2 py-0.5 rounded text-sm">
              {operadoresAtivos.length} activo{operadoresAtivos.length !== 1 ? 's' : ''}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSelectorModal(true)}
              className="text-white hover:bg-white/20"
            >
              <Plus className="h-4 w-4 mr-1" />
              Adicionar
            </Button>
            <button
              onClick={toggleMinimizar}
              className="p-1.5 hover:bg-white/20 rounded"
              title="Expandir"
            >
              <ChevronUp className="h-5 w-5" />
            </button>
            <button
              onClick={fecharSimulador}
              className="p-1.5 hover:bg-red-500/50 rounded"
              title="Fechar"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
        
        {/* Modal de Seleção - também disponível no modo minimizado */}
        <SelectorModal 
          open={showSelectorModal}
          onOpenChange={setShowSelectorModal}
          operadores={operadoresFiltrados}
          operadoresAtivos={operadoresAtivos}
          loading={loading}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onSelect={handleAdicionarOperador}
        />
      </div>
    );
  }

  // Modo expandido - painel flutuante completo
  return (
    <>
      <div 
        className="fixed inset-0 left-64 z-40 flex flex-col bg-stone-100"
        data-testid="simulador-expandido"
      >
        {/* Header */}
        <div className="p-4 border-b bg-white flex items-center justify-between shrink-0">
          <div className="flex items-center gap-4">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <Monitor className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-heading text-navy">Simulador de Operadores</h1>
              <p className="text-sm text-stone-500">
                {operadoresAtivos.length}/10 operadores activos • Em segundo plano
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Controlo do Grid */}
            <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
              {[2, 3, 4, 5].map(cols => (
                <button
                  key={cols}
                  onClick={() => setGridCols(cols)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    gridCols === cols
                      ? 'bg-white text-indigo-600 shadow-sm'
                      : 'text-stone-500 hover:text-stone-700'
                  }`}
                >
                  {cols}x
                </button>
              ))}
            </div>

            {operadoresAtivos.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={removerTodos}
                className="text-red-600 border-red-200 hover:bg-red-50"
              >
                <X className="h-4 w-4 mr-1" />
                Fechar Todos
              </Button>
            )}

            <Button
              onClick={() => setShowSelectorModal(true)}
              className="bg-indigo-600 hover:bg-indigo-700"
              disabled={operadoresAtivos.length >= 10}
            >
              <Plus className="h-4 w-4 mr-2" />
              Adicionar
            </Button>

            <div className="h-6 w-px bg-stone-300 mx-1" />

            <button
              onClick={toggleMinimizar}
              className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
              title="Minimizar"
            >
              <Minus className="h-5 w-5 text-stone-600" />
            </button>
            <button
              onClick={fecharSimulador}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors"
              title="Fechar Simulador"
            >
              <X className="h-5 w-5 text-red-600" />
            </button>
          </div>
        </div>

        {/* Área de Janelas */}
        <div className="flex-1 p-4 overflow-auto">
          {operadoresAtivos.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <Card className="p-8 text-center max-w-md">
                <Monitor className="h-16 w-16 text-stone-300 mx-auto mb-4" />
                <h2 className="text-lg font-semibold text-stone-700 mb-2">
                  Nenhum operador activo
                </h2>
                <p className="text-stone-500 mb-4">
                  Adicione operadores para simular o ambiente de produção.
                </p>
                <Button
                  onClick={() => setShowSelectorModal(true)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Operador
                </Button>
              </Card>
            </div>
          ) : fullscreenOperador ? (
            <div className="h-full">
              {operadoresAtivos.filter(o => o.id === fullscreenOperador).map(operador => (
                <OperadorWindowFlutuante
                  key={operador.id}
                  operador={operador}
                  onRemove={() => removerOperador(operador.id)}
                  onReload={() => recarregarOperador(operador.id)}
                  onToggleFullscreen={() => toggleFullscreen(operador.id)}
                  isFullscreen={true}
                />
              ))}
            </div>
          ) : (
            <div className={`grid ${getGridClass()} gap-4 h-full`}>
              {operadoresAtivos.map(operador => (
                <OperadorWindowFlutuante
                  key={operador.id}
                  operador={operador}
                  onRemove={() => removerOperador(operador.id)}
                  onReload={() => recarregarOperador(operador.id)}
                  onToggleFullscreen={() => toggleFullscreen(operador.id)}
                  isFullscreen={false}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Modal de Seleção */}
      <SelectorModal 
        open={showSelectorModal}
        onOpenChange={setShowSelectorModal}
        operadores={operadoresFiltrados}
        operadoresAtivos={operadoresAtivos}
        loading={loading}
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        onSelect={handleAdicionarOperador}
      />
    </>
  );
}

// Modal de Seleção de Operador
function SelectorModal({ open, onOpenChange, operadores, operadoresAtivos, loading, searchTerm, setSearchTerm, onSelect }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5 text-indigo-500" />
            Seleccionar Operador
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder="Pesquisar operador..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="max-h-[400px] overflow-y-auto space-y-2">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="h-6 w-6 text-stone-400 animate-spin" />
              </div>
            ) : operadores.length === 0 ? (
              <div className="text-center py-8 text-stone-500">
                Nenhum operador encontrado
              </div>
            ) : (
              operadores.map(operador => {
                const isActive = operadoresAtivos.find(o => o.id === operador.id);
                return (
                  <button
                    key={operador.id}
                    onClick={() => !isActive && onSelect(operador)}
                    disabled={isActive}
                    className={`w-full p-3 rounded-lg border text-left transition-colors flex items-center justify-between ${
                      isActive
                        ? 'bg-stone-100 border-stone-200 cursor-not-allowed opacity-60'
                        : 'bg-white border-stone-200 hover:border-indigo-300 hover:bg-indigo-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                        isActive ? 'bg-stone-400' : 'bg-indigo-500'
                      }`}>
                        {(operador.nome || operador.username || '?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-stone-800">{operador.nome || operador.username}</p>
                        <p className="text-sm text-stone-500">@{operador.username || 'N/A'}</p>
                      </div>
                    </div>
                    {isActive ? (
                      <span className="text-xs bg-stone-200 text-stone-600 px-2 py-1 rounded">
                        Já aberto
                      </span>
                    ) : (
                      <Plus className="h-5 w-5 text-indigo-500" />
                    )}
                  </button>
                );
              })
            )}
          </div>

          <div className="text-xs text-stone-400 text-center pt-2 border-t">
            {operadoresAtivos.length}/10 operadores activos •
            Cada janela simula a experiência real do operador
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Componente de Janela do Operador
function OperadorWindowFlutuante({ operador, onRemove, onReload, onToggleFullscreen, isFullscreen }) {
  const [isLoading, setIsLoading] = useState(false); // Começar com false para não bloquear

  const operadorUrl = `${window.location.origin}/sim/operador/${operador.username}?hideEmergentBadge=true`;
  const nomeOperador = operador.nome || operador.username || '?';

  if (isFullscreen) {
    return (
      <Card className="flex flex-col overflow-hidden h-full">
        <div className="flex items-center justify-between p-2 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white shrink-0">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
              {nomeOperador.charAt(0).toUpperCase()}
            </div>
            <span className="font-medium text-sm">{nomeOperador}</span>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={onReload} className="p-1.5 hover:bg-white/20 rounded" title="Recarregar">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button onClick={onToggleFullscreen} className="p-1.5 hover:bg-white/20 rounded" title="Minimizar">
              <Shrink className="h-4 w-4" />
            </button>
            <button onClick={onRemove} className="p-1.5 hover:bg-red-500/50 rounded" title="Fechar">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 relative bg-white">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-stone-50 z-10">
              <RefreshCw className="h-8 w-8 text-indigo-500 animate-spin" />
            </div>
          )}
          <iframe
            id={`iframe-sim-${operador.id}`}
            src={operadorUrl}
            className="w-full h-full border-0"
            onLoad={() => setIsLoading(false)}
            title={`Operador ${nomeOperador}`}
          />
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-col items-center">
      <div className="relative">
        <div className="bg-stone-800 rounded-[2.5rem] p-2 shadow-xl">
          <div className="absolute top-4 left-1/2 -translate-x-1/2 w-20 h-6 bg-stone-900 rounded-full z-20" />

          <div className="bg-white rounded-[2rem] overflow-hidden relative" style={{ width: '364px', height: '728px' }}>
            <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-3 py-1.5 bg-gradient-to-r from-indigo-600 to-indigo-700 text-white">
              <div className="flex items-center gap-1.5">
                <div className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[10px] font-bold">
                  {nomeOperador.charAt(0).toUpperCase()}
                </div>
                <span className="font-medium text-xs truncate max-w-[80px]">{nomeOperador}</span>
              </div>
              <div className="flex items-center gap-0.5">
                <button onClick={onReload} className="p-1 hover:bg-white/20 rounded" title="Recarregar">
                  <RefreshCw className="h-3 w-3" />
                </button>
                <button onClick={onToggleFullscreen} className="p-1 hover:bg-white/20 rounded" title="Maximizar">
                  <Expand className="h-3 w-3" />
                </button>
                <button onClick={onRemove} className="p-1 hover:bg-red-500/50 rounded" title="Fechar">
                  <X className="h-3 w-3" />
                </button>
              </div>
            </div>

            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-stone-50 z-10">
                <div className="text-center">
                  <RefreshCw className="h-6 w-6 text-indigo-500 animate-spin mx-auto mb-2" />
                  <p className="text-xs text-stone-500">A carregar...</p>
                </div>
              </div>
            )}

            <iframe
              id={`iframe-sim-${operador.id}`}
              src={operadorUrl}
              className="w-full h-full border-0"
              style={{ marginTop: '28px', height: 'calc(100% - 28px)' }}
              onLoad={() => setIsLoading(false)}
              title={`Operador ${nomeOperador}`}
            />
          </div>
        </div>

        <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-10 h-1 bg-stone-600 rounded-full" />
      </div>
    </div>
  );
}
